/**
 * A custom element that toggles content based on a target date/time, once
 * per second. Three independent modes (combinable via data attributes):
 *
 * - `data-hide-while-active`: hides the whole element until the target date
 *   has passed. Used to keep the shipping estimate note off coming-soon
 *   product pages while the countdown is still running.
 * - `data-hide-when-expired`: hides the whole element once the target date
 *   has passed. Used to remove the mini countdown from a coming-soon
 *   product page once it's done counting down.
 * - Swap mode: shows the `[data-gate-active]` child before the target date
 *   and the `[data-gate-expired]` child after it. Used to swap the
 *   "Notify me" form back to the normal add-to-cart button.
 *
 * All three read the same shared `data-target` date, normally sourced from
 * the global "Coming soon" theme setting, so a product's buy button, its
 * shipping note and its mini countdown all flip at the same moment.
 */
class ComingSoonGate extends HTMLElement {
  connectedCallback() {
    this.targetDate = new Date(this.dataset.target);
    if (isNaN(this.targetDate.getTime())) return;

    this.activeEl = this.querySelector(':scope > [data-gate-active]');
    this.expiredEl = this.querySelector(':scope > [data-gate-expired]');

    this.#tick();
    this.interval = setInterval(this.#tick, 1000);
  }

  disconnectedCallback() {
    clearInterval(this.interval);
  }

  #tick = () => {
    const expired = Date.now() >= this.targetDate.getTime();

    if ('hideWhileActive' in this.dataset) this.hidden = !expired;
    if ('hideWhenExpired' in this.dataset) this.hidden = expired;
    if (this.activeEl) this.activeEl.hidden = expired;
    if (this.expiredEl) this.expiredEl.hidden = !expired;

    if (expired) clearInterval(this.interval);
  };
}

if (!customElements.get('coming-soon-gate')) {
  customElements.define('coming-soon-gate', ComingSoonGate);
}
